import { useEffect, useId, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import {
  DataTable,
  DataTableSkeleton,
  ExportLink,
  dataTableFeatures,
} from "@/components/ui/data-table";
import { Filterbalk } from "@/components/ui/data-table-filters";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pager } from "@/components/ui/pager";
import { FULL_PAGE_SIZE } from "@/lib/paging";
import type { Filterdescriptor, Filterstand } from "@/components/ui/data-table-filter";
import { naarZoekparameters } from "@/components/ui/data-table-filter";
import { useZoekPersonen } from "./useKoppelingen";

type Persoon = NonNullable<ReturnType<typeof useZoekPersonen>["data"]>["items"][number];

type PersoonFilters = {
  descriptors: Filterdescriptor<Persoon>[];
  stand: Filterstand;
  onStand: (stand: Filterstand) => void;
};

const helper = createColumnHelper<typeof dataTableFeatures, Persoon>();

/**
 * Korpsen under the naam, because a third column wraps every name in this shared pane. Neither
 * sorts: the server fixes the order and takes no `sortBy`, so a control could only fight it.
 */
const kolommen = (t: TFunction<"koppelingen">) =>
  helper.columns([
    helper.accessor("naam", {
      header: t("zoeker.naam"),
      enableSorting: false,
      cell: (info) => (
        <span className="block">
          <span className="font-medium">{info.getValue()}</span>
          {info.row.original.isBeheerder && (
            <span className="font-medium">{t("zoeker.beheerder")}</span>
          )}
          {info.row.original.korpsen.length > 0 && (
            <span className="block text-xs text-muted-foreground">
              {info.row.original.korpsen.join(", ")}
            </span>
          )}
        </span>
      ),
    }),
    helper.accessor("email", {
      header: t("zoeker.email"),
      enableSorting: false,
      // Wraps rather than widening the column: an address has no natural break in it, so
      // without this one long enough forces the whole table into a sideways scroll instead.
      cell: (info) => <span className="text-xs break-all">{info.getValue()}</span>,
    }),
  ]);

/**
 * Adjusted during render rather than in an effect, per React's own guidance for "reset state when
 * a prop changes": an effect would run the stale-page render first and only fix it a frame later,
 * which is the flash of the wrong page this avoids entirely.
 */
function usePaginaResetBij(waarde: string, setPage: (pagina: number) => void) {
  const [voorgaande, setVoorgaande] = useState(waarde);
  if (waarde !== voorgaande) {
    setVoorgaande(waarde);
    setPage(1);
  }
}

/**
 * The box renders its own state, not `term`. A caller that keeps the term in the URL answers a
 * keystroke a render late - the router lands the navigation in a transition - and a controlled
 * input rewritten to the value before the one just typed puts the caret back where that value
 * ended. Typing "noorman@jeugdbrandweer.nl" came out as "no@jeugdbrandweer.nlorman".
 */
function useGetypteTerm(term: string, onZoek: (term: string) => void) {
  const [getypt, setGetypt] = useState(term);
  const onderweg = useRef<string[]>([]);

  // A term the caller changed itself - a link opened on this screen, a reset, the back button -
  // is still the caller's to give, so it wins here. Its own keystrokes coming back are not: they
  // arrive in order and late, so each one is matched off the queue of what was sent, along with
  // anything still queued in front of it. Once the queue is empty the caller has caught up, and
  // the next term to arrive is one it thought of by itself.
  useEffect(() => {
    const positie = onderweg.current.indexOf(term);
    if (positie >= 0) {
      onderweg.current = onderweg.current.slice(positie + 1);
      return;
    }
    onderweg.current = [];
    setGetypt(term);
  }, [term]);

  const zoek = (nieuw: string) => {
    onderweg.current = [...onderweg.current, nieuw];
    setGetypt(nieuw);
    onZoek(nieuw);
  };

  return { getypt, zoek };
}

/**
 * Nothing is ever pre-selected: the account's own claims are the one thing that could seed a
 * suggestion, and are exactly what ADR-0018 declined to trust. The term is the caller's because
 * it lives in the URL beside the chosen persoon. See docs/adr/0018-identity-linking.md.
 */
export function PersoonZoeker({
  term,
  onZoek,
  gekozen,
  onKies,
  disabled,
  toonIedereen = false,
  pagina,
  onPaginaChange,
  filters,
  inschrijfperiodeId,
  alleenOndersteuners,
  exportable = false,
}: {
  term: string;
  onZoek: (term: string) => void;
  gekozen: string | null;
  onKies: (persoonId: string | null) => void;
  disabled?: boolean;
  /** Lists everybody up front and matches an e-mailadres too: for choosing a persoon, not identifying one. */
  toonIedereen?: boolean;
  /** Passed only by a caller that is a full page of its own; left out, the page is local state. */
  pagina?: number;
  onPaginaChange?: (pagina: number) => void;
  /**
   * Narrowing beyond the box above. Server-side, so the descriptors carry no `past` and the
   * facets show no counts: one page of an answer the server already narrowed.
   * Bevoegdheden and Ondersteuners are the only callers that pass any so far.
   */
  filters?: PersoonFilters;
  /** The window a `reedsIngeschreven` filter checks against - the ondersteuners screen's own. */
  inschrijfperiodeId?: string;
  /** Only VBS contacts and Portaal juryleden: the ondersteuners and bevoegdheden screens. */
  alleenOndersteuners?: boolean;
  /** A list in its own right, like the ondersteuners screen, rather than a picker. */
  exportable?: boolean;
}) {
  const { t } = useTranslation("koppelingen");

  const [localPage, setLocalPage] = useState(1);
  const page = pagina ?? localPage;
  const setPage = onPaginaChange ?? setLocalPage;

  // FULL_PAGE_SIZE, not MODAL_PAGE_SIZE: this is not a compact dialog widget. It is the primary
  // content of two full pages and the roomiest part of a third, so it gets the same page size
  // every other full-page list does.
  // `isLoading` (isPending *and* actually fetching), not `isPending` alone: below two letters
  // the query is `enabled: false` and has never run, which is `isPending` too - and that must
  // still draw the "type two letters" hint below, not a skeleton for a fetch that isn't happening.
  // The descriptor ids are the query parameter names, so this is the whole of the mapping.
  const functieIds = filters
    ? (naarZoekparameters(filters.stand).functie as string[] | undefined)
    : undefined;
  const reedsIngeschreven = filters
    ? (naarZoekparameters(filters.stand).reedsIngeschreven as string | undefined)
    : undefined;

  const { data: resultaten, isLoading } = useZoekPersonen(
    term,
    toonIedereen,
    page,
    FULL_PAGE_SIZE,
    functieIds,
    inschrijfperiodeId,
    reedsIngeschreven,
    alleenOndersteuners,
  );
  const id = useId();

  const columns = kolommen(t);

  // A new search - the term the query actually runs on, not every keystroke in the box below -
  // starts on page one. Landing a new search on whatever page the last one happened to end on
  // would be confusing, and this component has no URL of its own to remember it in anyway.
  usePaginaResetBij(term, setPage);

  // A filter narrows the set the server counts, exactly as the term above does, so page 2 of
  // the old set would otherwise land past the end of the new, shorter one.
  const sleutel = `${(functieIds ?? []).join(",")}|${reedsIngeschreven ?? ""}`;
  usePaginaResetBij(sleutel, setPage);

  const { getypt, zoek } = useGetypteTerm(term, onZoek);

  const exportLink = exportable
    ? exportHref("/api/beheer/koppelingen/personen/export", {
        query: {
          q: term,
          includeEmail: toonIedereen,
          functie: functieIds,
          inschrijfperiodeId,
          reedsIngeschreven,
          alleenOndersteuners,
        },
      })
    : undefined;

  // Filters, then the search box with the export beside it: the order DataTable draws on every
  // other list. Held here rather than in DataTable so the box stays mounted while a page loads.
  const inhoud = (
    <>
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid min-w-48 flex-1 gap-2">
          <Label htmlFor={`${id}-zoeken`} className="sr-only">
            {toonIedereen ? t("zoeker.labelIedereen") : t("zoeker.labelNaam")}
          </Label>
          {/* No width of its own: every other search box in the backoffice - the one DataTable
              draws, the one a keuzelijst draws - fills the pane it sits in, and a box capped
              narrower than the table under it reads as a box that failed to finish drawing. */}
          <Input
            id={`${id}-zoeken`}
            type="search"
            value={getypt}
            placeholder={
              toonIedereen ? t("zoeker.placeholderIedereen") : t("zoeker.placeholderNaam")
            }
            disabled={disabled}
            onChange={(event) => zoek(event.target.value)}
          />
        </div>
        {exportLink && <ExportLink href={exportLink} />}
      </div>

      {/* Only where a search is required: elsewhere the narrowed list is its own answer. */}
      {!toonIedereen && getypt.trim().length > 0 && getypt.trim().length < 2 && (
        <p className="text-sm text-muted-foreground">{t("zoeker.minimaal")}</p>
      )}

      <PersoonZoekerResultaten
        t={t}
        columns={columns}
        resultaten={resultaten}
        isLoading={isLoading}
        gekozen={gekozen}
        onKies={onKies}
        disabled={disabled}
        onPaginaChange={setPage}
      />
    </>
  );

  // Server-side descriptors carry no `past`, so there are no rows to count facets against.
  return filters ? (
    <Filterbalk
      descriptors={filters.descriptors}
      rijen={[]}
      stand={filters.stand}
      onStand={filters.onStand}
    >
      {inhoud}
    </Filterbalk>
  ) : (
    <div className="space-y-3">{inhoud}</div>
  );
}

function PersoonZoekerResultaten({
  t,
  columns,
  resultaten,
  isLoading,
  gekozen,
  onKies,
  disabled,
  onPaginaChange,
}: {
  t: TFunction<"koppelingen">;
  columns: ReturnType<typeof kolommen>;
  resultaten: ReturnType<typeof useZoekPersonen>["data"];
  isLoading: boolean;
  gekozen: string | null;
  onKies: (persoonId: string | null) => void;
  disabled?: boolean;
  onPaginaChange: (pagina: number) => void;
}) {
  if (isLoading) {
    return (
      <DataTableSkeleton
        label={t("zoeker.resultaten")}
        columns={columns}
        rows={3}
        twoLine={["naam"]}
        busy={t("zoeker.bezig")}
      />
    );
  }

  if (!resultaten) return null;

  return (
    <>
      <DataTable
        label={t("zoeker.resultaten")}
        data={resultaten.items}
        columns={columns}
        getRowId={(persoon) => persoon.persoonId}
        selectedId={gekozen ?? undefined}
        onSelect={(persoonId) => {
          if (!disabled) onKies(persoonId);
        }}
        empty={t("zoeker.leeg")}
      />
      <Pager
        page={resultaten.page}
        pageSize={resultaten.pageSize}
        totalCount={resultaten.totalCount}
        onPageChange={onPaginaChange}
      />
    </>
  );
}
